/**
 * Clubs (`D277`, `D278`, `D279`): route functions, query keys and the
 * mutations behind `features/clubs`.
 *
 * ── ONLINE ONLY ─────────────────────────────────────────────────────────────
 * Nothing here goes through the workout's outbox (`D163`): a club is a
 * conversation among people, and a join or a message that lands hours later
 * is the wrong one. The writes that create something carry the client's
 * UUIDv7 (a club, a message), so a resend after a timeout is the same row.
 *
 * ── THE WRITES READ NO BODY ─────────────────────────────────────────────────
 * Every write is followed by a refetch of what it changed (`invalidates`),
 * and the screen draws from the refetch — so the writes go through
 * `sendNoContent` and never depend on the shape of their own answer.
 */
import { CLUB_ROUTES, clubDetailOutput, clubDirectoryOutput, clubInviteOutput, clubInvitePath, clubListOutput, clubMessagesOutput, clubPath, } from "@repo/contract/community";
import { getJson, sendJson, sendNoContent, } from "../core/request";
export const clubKeys = {
    all: ["clubs"],
    list: () => ["clubs", "list"],
    directory: () => ["clubs", "directory"],
    invite: (code) => ["clubs", "invite", code],
    club: (clubId) => ["clubs", "club", clubId],
    messages: (clubId) => ["clubs", "messages", clubId],
};
function sub(clubId, rest) {
    return `${clubPath(clubId)}/${rest}`;
}
export function clubListOptions(api) {
    return {
        queryFn: ({ signal }) => getJson(api, CLUB_ROUTES.clubs, clubListOutput, { signal }),
        queryKey: clubKeys.list(),
        staleTime: 60_000,
    };
}
export function clubDirectoryOptions(api) {
    return {
        queryFn: ({ signal }) => getJson(api, CLUB_ROUTES.directory, clubDirectoryOutput, { signal }),
        queryKey: clubKeys.directory(),
        staleTime: 5 * 60_000,
    };
}
export function clubDetailOptions(api, clubId) {
    return {
        queryFn: ({ signal }) => getJson(api, clubPath(clubId), clubDetailOutput, { signal }),
        queryKey: clubKeys.club(clubId),
        staleTime: 30_000,
    };
}
export function clubMessagesOptions(api, clubId) {
    return {
        queryFn: ({ signal }) => getJson(api, sub(clubId, "messages"), clubMessagesOutput, { signal }),
        queryKey: clubKeys.messages(clubId),
        staleTime: 15_000,
    };
}
/** `GET /me/clubs/invite/:code` — called on demand, never cached long:
 *  the settings it shows are the ones the join must name (`D234`). */
export function fetchClubInvite(api, code) {
    return getJson(api, clubInvitePath(code), clubInviteOutput);
}
/** `POST /me/clubs` — answers the club, 201 new or 200 for a resend. */
export function createClubMutation(api) {
    return {
        invalidates: [clubKeys.list()],
        mutationFn: (input) => sendJson(api, "POST", CLUB_ROUTES.clubs, input, clubDetailOutput),
        mutationKey: ["clubs", "create"],
    };
}
export function joinClubMutation(api) {
    return {
        invalidates: [clubKeys.list(), clubKeys.directory()],
        mutationFn: (input) => sendJson(api, "POST", CLUB_ROUTES.join, input, clubDetailOutput),
        mutationKey: ["clubs", "join"],
    };
}
export function leaveClubMutation(api) {
    return {
        invalidates: [clubKeys.all],
        mutationFn: ({ clubId }) => sendNoContent(api, "POST", sub(clubId, "leave")),
        mutationKey: ["clubs", "leave"],
    };
}
/** The writes on one club. Each refetches the club, and the list where a
 *  name or a round shows there too. */
export function clubSettingsMutation(api, clubId) {
    return {
        invalidates: [clubKeys.club(clubId), clubKeys.list(), clubKeys.directory()],
        mutationFn: (input) => sendNoContent(api, "PUT", sub(clubId, "settings"), input),
        mutationKey: ["clubs", "settings", clubId],
    };
}
export function clubRoundMutation(api, clubId) {
    return {
        invalidates: [clubKeys.club(clubId), clubKeys.list()],
        mutationFn: (input) => sendNoContent(api, "POST", sub(clubId, "rounds"), input),
        mutationKey: ["clubs", "round", clubId],
    };
}
export function clubMemberMutation(api, clubId) {
    return {
        invalidates: [clubKeys.club(clubId), clubKeys.list()],
        mutationFn: (change) => {
            const path = sub(clubId, `members/${encodeURIComponent(change.memberId)}`);
            return "remove" in change
                ? sendNoContent(api, "DELETE", path)
                : sendNoContent(api, "PUT", path, change.input);
        },
        mutationKey: ["clubs", "member", clubId],
    };
}
export function clubReactionMutation(api, clubId) {
    return {
        invalidates: [clubKeys.club(clubId)],
        mutationFn: (input) => sendNoContent(api, "PUT", sub(clubId, "reactions"), input),
        mutationKey: ["clubs", "reaction", clubId],
    };
}
export function clubMessageMutation(api, clubId) {
    return {
        invalidates: [clubKeys.messages(clubId)],
        mutationFn: (input) => sendNoContent(api, "POST", sub(clubId, "messages"), input),
        mutationKey: ["clubs", "message", clubId],
    };
}
/** Delete (the author, or an admin) or report one message. */
export function clubMessageActionMutation(api, clubId) {
    return {
        invalidates: [clubKeys.messages(clubId)],
        mutationFn: ({ action, messageId }) => {
            const path = sub(clubId, `messages/${encodeURIComponent(messageId)}`);
            return action === "delete"
                ? sendNoContent(api, "DELETE", path)
                : sendNoContent(api, "POST", `${path}/report`);
        },
        mutationKey: ["clubs", "message-action", clubId],
    };
}
//# sourceMappingURL=clubs.js.map